import axios from 'axios';

// The base URL is now correctly handled by the global axios instance in api.js
// We will create an isolated instance but use the same base URL logic.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://care360-backend.onrender.com/api/v1';

/**
 * Patient Portal Service
 * Handles all API calls related to patient portal functionality
 */
class PatientPortalService {
  constructor() {
    // Create isolated axios instance for portal API calls
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000, // 10 second timeout
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('portalAuthToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle portal-specific errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle token expiration for portal requests only
        if (error.response?.status === 401) {
          console.warn('Portal service: Authentication failed, clearing portal token');
          localStorage.removeItem('portalAuthToken');

          // Only redirect if we're currently on a portal page
          if (window.location.pathname.startsWith('/portal') &&
              !window.location.pathname.includes('/login')) {
            console.log('Portal service: Redirecting to portal login');
            window.location.href = '/portal/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // --- Portal Authentication ---

  /**
   * Login to patient portal
   * @param {string} accessToken - Portal access token from URL
   * @param {string} password - Portal password
   * @returns {Promise} - Login response
   */
  async login(accessToken, password) {
    try {
      // Use the isolated 'this.api' instance which has the correct baseURL
      const response = await this.api.post(`/portal/login/${accessToken}`, {
        password
      });
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('portalAuthToken', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      console.error('Portal login error:', error);
      throw error;
    }
  }

  /**
   * Logout from patient portal
   */
  logout() {
    localStorage.removeItem('portalAuthToken');
  }

  // --- Portal Data Access ---

  /**
   * Get patient health overview
   * @returns {Promise} - Health overview data
   */
  async getHealthOverview() {
    try {
      const response = await this.api.get('/portal/health-overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching health overview:', error);
      throw error;
    }
  }

  /**
   * Get patient timeline
   * @returns {Promise} - Timeline data
   */
  async getTimeline() {
    try {
      const response = await this.api.get('/portal/timeline');
      return response.data;
    } catch (error) {
      console.error('Error fetching timeline:', error);
      throw error;
    }
  }

  // --- Admin Functions (for clinicians) ---

  /**
   * Generate portal access for a patient (admin only)
   * @param {string} patientId - Patient ID
   * @param {number} expirationDays - Days until expiration
   * @returns {Promise} - Generated access credentials
   */
  async generatePortalAccess(patientId, expirationDays = 90) {
    try {
      // First, get the current status. This will create a portal access if it doesn't exist.
      const statusData = await this.getPortalStatus(patientId);
      
      // If we want to update the expiration, we can add a new endpoint or modify an existing one.
      // For now, we'll just return the existing credentials.
      if (statusData.hasPortalAccess) {
        const credentials = await this.getCurrentPortalCredentials(patientId);
        return {
            ...credentials,
            message: 'Portal access already exists. Returning current credentials.'
        };
      }

      // This part of the code may now be redundant if getPortalStatus creates the access.
      // However, we can keep it as a fallback or for explicit generation with specific parameters.
      const token = localStorage.getItem('authToken');
      const response = await this.api.post(
        `/portal/admin/generate/${patientId}`,
        { expirationDays },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating portal access:', error);
      throw error;
    }
  }

  /**
   * Disable portal access for a patient (admin only)
   * @param {string} patientId - Patient ID
   * @returns {Promise} - Response
   */
  async disablePortalAccess(patientId) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.api.patch(
        `/portal/admin/disable/${patientId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error disabling portal access:', error);
      throw error;
    }
  }

  /**
   * Get portal access status for a patient (admin only)
   * @param {string} patientId - Patient ID
   * @returns {Promise} - Portal status data
   */
  async getPortalStatus(patientId) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.api.get(
        `/portal/admin/status/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching portal status:', error);
      throw error;
    }
  }

  /**
   * Get current portal credentials for a patient (admin only)
   * @param {string} patientId - Patient ID
   * @returns {Promise} - Current portal credentials
   */
  async getCurrentPortalCredentials(patientId) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.api.get(
        `/portal/admin/credentials/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching current portal credentials:', error);
      throw error;
    }
  }

  /**
   * Extend portal access for a patient (admin only)
   * @param {string} patientId - Patient ID
   * @param {number} expirationDays - Days to extend (default 90)
   * @returns {Promise} - Extended access data
   */
  async extendPortalAccess(patientId, expirationDays = 90) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.api.patch(
        `/portal/admin/extend/${patientId}`,
        { expirationDays },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error extending portal access:', error);
      throw error;
    }
  }

  // --- Utility Functions ---

  /**
   * Check if user is authenticated to portal
   * @returns {boolean} - Authentication status
   */
  isAuthenticated() {
    const token = localStorage.getItem('portalAuthToken');
    return !!token;
  }

  /**
   * Get current portal auth token
   * @returns {string|null} - Auth token or null
   */
  getAuthToken() {
    return localStorage.getItem('portalAuthToken');
  }

  /**
   * Validate access token format
   * @param {string} token - Token to validate
   * @returns {boolean} - True if valid format
   */
  isValidTokenFormat(token) {
    return /^[a-f0-9]{64}$/i.test(token);
  }

  /**
   * Get portal URL for sharing
   * @param {string} accessToken - Access token
   * @param {string} baseUrl - Base URL (optional)
   * @returns {string} - Complete portal URL
   */
  getPortalUrl(accessToken, baseUrl = window.location.origin) {
    return `${baseUrl}/portal/login/${accessToken}`;
  }
}

// Create and export a singleton instance
const patientPortalService = new PatientPortalService();
export default patientPortalService;

// Also export the class for testing purposes
export { PatientPortalService };
